import { Expert } from "@/types/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsApiReady } from "../hooks/use-api-ready";
import {
  type GetGroupExpertsResponseDto,
  type GetKitExpertsResponseDto,
  groupKitExpertControllerCreate,
  groupKitExpertControllerGetExperts,
  groupKitExpertControllerGetRemovedExperts,
  groupKitExpertControllerGetSingle,
  groupKitExpertControllerPatchExpert,
} from "../lib/gen-api";
import { defaultIfUndefined } from "../lib/nullable";
import { queryKeys } from "../lib/query-keys";

/**
 * Will give all the experts for a specific kit, or all group experts if no kit is provided.
 * The data hook is null-safe meaning it will return an empty array if the groupUuid is null or undefined.
 */
export const useExpertsData = (
  groupUuid?: string | null,
  kitUuid?: string | null,
) => {
  const queryClient = useQueryClient();

  const {
    data: experts,
    isPending: isLoading,
    error,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid,
    queryKey: queryKeys.kitExperts(groupUuid!, kitUuid!),
    queryFn: () => {
      return groupKitExpertControllerGetExperts({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
        },
      }).then((res) => res.data?.data ?? []);
    },
  });

  const { mutateAsync: createExpert, isPending: isCreatingExpert } =
    useMutation({
      mutationFn: async (vars: { name: string }) => {
        if (!groupUuid || !kitUuid) {
          throw new Error(
            "Group UUID and Kit UUID are required for creating experts",
          );
        }
        const expert = await groupKitExpertControllerCreate({
          path: {
            groupUuid,
            kitUuid,
          },
          body: {
            name: vars.name,
          },
        });

        // Invalidate both kit-specific and group-wide expert queries
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitExperts(groupUuid!, kitUuid!),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.groupExperts(groupUuid!),
        });

        return expert;
      },
    });

  return {
    experts: experts ?? [],
    isLoading,
    error,
    createExpert,
    isCreatingExpert,
  };
};

export const useExpertData = (
  groupUuid: string | null,
  kitUuid: string | null,
  expertUuid: string | null,
) => {
  const queryClient = useQueryClient();

  const {
    data: expert,
    isLoading,
    error,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid && !!expertUuid,
    initialData: null,
    queryKey: queryKeys.expert(groupUuid!, kitUuid!, expertUuid!),
    queryFn: () =>
      groupKitExpertControllerGetSingle({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
          expertUuid: expertUuid!,
        },
      }).then((res) => res.data ?? null),
  });

  const { mutateAsync: updateExpertSkill } = useMutation({
    mutationFn: async () => {
      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.expertContent(groupUuid!, kitUuid!, expertUuid!),
      });
    },
  });

  const { mutateAsync: updateExpert } = useMutation({
    mutationFn: async (vars: {
      name?: string;
      description?: string;
      context?: string;
      toneOfVoice?: string;
      profilePicture?: string | null;
      folderUuid?: string | null;
      expertType?: Expert["expertType"];
    }) => {
      if (!expert) {
        throw new Error("Expert not found");
      }

      return groupKitExpertControllerPatchExpert({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
          expertUuid: expertUuid!,
        },
        body: {
          name: vars.name,
          context: vars.context ?? expert.context,
          toneOfVoice: vars.toneOfVoice ?? expert.toneOfVoice,
          description: vars.description ?? expert.description,
          folderUuid: vars.folderUuid ?? expert.folderUuid ?? null,
          expertType: vars.expertType ?? expert.expertType,
          profilePicture: defaultIfUndefined(
            vars.profilePicture,
            expert.profilePicture,
          ),
        },
      });
    },
    onMutate: async (vars) => {
      if (!expert || !groupUuid || !kitUuid || !expertUuid) {
        return;
      }

      const queriesToCancel = [
        queryKeys.expert(groupUuid, kitUuid, expertUuid),
        queryKeys.kitExperts(groupUuid, kitUuid),
        queryKeys.groupExperts(groupUuid),
      ];
      await Promise.all(
        queriesToCancel.map((queryKey) =>
          queryClient.cancelQueries({ queryKey }),
        ),
      );

      // Snapshot the previous values for rollback
      const previousExpert = queryClient.getQueryData(
        queryKeys.expert(groupUuid, kitUuid, expertUuid),
      );
      const previousKitExperts = queryClient.getQueryData(
        queryKeys.kitExperts(groupUuid, kitUuid),
      );
      const previousGroupExperts = queryClient.getQueryData(
        queryKeys.groupExperts(groupUuid),
      );

      // Create the optimistic update data
      const optimisticExpert = {
        ...expert,
        name: vars.name ?? expert.name,
        description: vars.description ?? expert.description,
        context: vars.context ?? expert.context,
        toneOfVoice: vars.toneOfVoice ?? expert.toneOfVoice,
        profilePicture: vars.profilePicture ?? expert.profilePicture,
      };
      const optimisticListFields = {
        name: vars.name ?? expert.name,
        description: vars.description ?? expert.description,
        expertType: vars.expertType ?? expert.expertType,
        profilePicture: defaultIfUndefined(
          vars.profilePicture,
          expert.profilePicture,
        ),
      };

      // Optimistically update the individual expert
      queryClient.setQueryData(
        queryKeys.expert(groupUuid, kitUuid, expertUuid),
        optimisticExpert,
      );

      // Optimistically update the expert in the kit experts list
      queryClient.setQueryData(
        queryKeys.kitExperts(groupUuid, kitUuid),
        (old: GetKitExpertsResponseDto["data"] = []) =>
          old.map((exp) =>
            exp.uuid === expertUuid ? { ...exp, ...optimisticListFields } : exp,
          ),
      );

      // Optimistically update the expert in the group experts list
      queryClient.setQueryData(
        queryKeys.groupExperts(groupUuid),
        (old: GetGroupExpertsResponseDto["data"] = []) =>
          old.map((exp) =>
            exp.uuid === expertUuid ? { ...exp, ...optimisticListFields } : exp,
          ),
      );

      // Return context for potential rollback
      return {
        previousExpert,
        previousKitExperts,
        previousGroupExperts,
      };
    },
    onError: (err, vars, context) => {
      // Rollback the optimistic updates on error
      if (context && groupUuid && kitUuid && expertUuid) {
        queryClient.setQueryData(
          queryKeys.expert(groupUuid, kitUuid, expertUuid),
          context.previousExpert,
        );
        queryClient.setQueryData(
          queryKeys.kitExperts(groupUuid, kitUuid),
          context.previousKitExperts,
        );
        queryClient.setQueryData(
          queryKeys.groupExperts(groupUuid),
          context.previousGroupExperts,
        );
      }
    },
    onSettled: () => {
      // Always refetch after mutation to ensure consistency
      if (groupUuid && kitUuid && expertUuid) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.expert(groupUuid, kitUuid, expertUuid),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitExperts(groupUuid, kitUuid),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.groupExperts(groupUuid),
        });
      }
    },
  });

  return {
    expert,
    isLoading,
    error,
    updateExpert,
    updateExpertSkill,
  };
};

/**
 * List experts used in chat but got removed from the kit.
 */
export function useRemovedKitExpertsData(
  groupUuid: string | null,
  kitUuid: string | null,
) {
  const { data: experts, isLoading } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid,
    initialData: [],
    queryKey: queryKeys.kitRemovedExperts(groupUuid!, kitUuid!),
    queryFn: async () => {
      const response = await groupKitExpertControllerGetRemovedExperts({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
        },
      });
      return response.data?.data ?? [];
    },
  });

  return {
    experts,
    isLoading,
  };
}
